import { SpoModelsResponse, SpoModelsResponseItem } from '@spo/types';
import { createColumnHelper } from '@tanstack/react-table';
import { VtmnAccordion } from '@vtmn/react';
import { format } from 'date-fns';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import genericImage from '../../../../assets/genericImage.jpeg';
import { environment } from '../../../../environments/environment';
import { usePixlImages } from '../../../../hooks/usePixlImages';
import { buildLegacyUrl } from '../../../../utils/legacyUrl';
import { DateCellTable } from '../../dateCellTable';
import { ModelAccordionHeader } from '../modelAccordionHeader';
import { ModelTagStatus } from '../modelTagStatus';
import { TableView } from '../tableView';
import './modelAccordion.scss';

interface ModelAccordionProps {
  smId: string;
  description: string;
  offerDate: string | undefined;
  models: SpoModelsResponse;
  selectedFiltersStatus?: string[];
  selectedModelIds?: string[];
  setSelectedModelIds?: React.Dispatch<React.SetStateAction<string[]>>;
  withCheckboxes?: boolean;
}
const renderModelTagStatus = (status: string) => (
  <ModelTagStatus status={status} />
);
const columnHelper = createColumnHelper<SpoModelsResponseItem>();

export const ModelAccordion = ({
  smId,
  description,
  offerDate,
  models,
  selectedFiltersStatus,
  selectedModelIds = [],
  setSelectedModelIds,
  withCheckboxes = false,
}: ModelAccordionProps) => {
  const { t } = useTranslation();
  const [isAccordionDeployed, setIsAccordionDeployed] = useState(false);
  const contentMediaUrl = environment.contentMediaUrl;

  const columnsModel = useMemo(
    () => [
      columnHelper.accessor('id', {
        header: 'N° model',
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('descriptionEn', {
        header: 'Label',
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('seasonality', {
        header: 'Seasonality',
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('beginDate', {
        header: 'Start date',
        cell: (info) => (
          <DateCellTable date={info.getValue()} extremumDate="start" />
        ),
      }),
      columnHelper.accessor('endDate', {
        header: 'End date',
        cell: (info) => format(new Date(info.getValue()), 'dd/MM/yyyy'),
      }),
      columnHelper.accessor('status', {
        header: 'Status',
        cell: (info) => renderModelTagStatus(info.getValue()),
      }),
    ],
    [],
  );

  const modelCodes = models.map((model) => model.id);

  const { data: pixlData, isFetching } = usePixlImages({
    modelCodes,
    width: 120,
    height: 120,
    contentMediaUrl,
  });

  const onCellClick = (row: SpoModelsResponseItem) => {
    window.open(
      buildLegacyUrl(row.superModelId, row.beginDate, row.endDate, row.id),
      '_blank',
    );
  };

  const filterModels = (
    modelData: SpoModelsResponse,
    selectedFiltersStatus: string[],
    smId: string,
  ) => {
    const statusFilters = selectedFiltersStatus.map((status) =>
      status.toUpperCase(),
    );
    return modelData
      .filter((model) => model.superModelId === smId)
      .filter((model) => statusFilters.includes(model.status.toUpperCase()));
  };

  const toggleAllModels = (newCheckStatus: boolean) => {
    const fullModelIds = models.map((model) => model.id);
    const updateSelectedModels: string[] = newCheckStatus
      ? [...new Set([...selectedModelIds, ...fullModelIds])]
      : [
          ...selectedModelIds.filter(
            (modelId) => !fullModelIds.includes(modelId),
          ),
        ];
    setSelectedModelIds?.(updateSelectedModels);
  };

  const renderModels = (models: SpoModelsResponse): React.JSX.Element => {
    const selectModelId = (newEntityId: string, referenceArray: string[]) => {
      const shadowModelIds = [...referenceArray];
      if (!referenceArray.includes(newEntityId)) {
        shadowModelIds.push(newEntityId);
      } else {
        shadowModelIds.splice(shadowModelIds.indexOf(newEntityId), 1);
      }
      setSelectedModelIds?.(shadowModelIds);
    };

    const checkboxRelatedProps = withCheckboxes
      ? {
          selectId: selectModelId,
          selectedIds: selectedModelIds,
          toggleAll: toggleAllModels,
          massEdit: false,
        }
      : {
          massEdit: true,
        };

    if (models?.length > 0) {
      const displayedModels =
        selectedFiltersStatus && selectedFiltersStatus?.length > 0
          ? filterModels(models, selectedFiltersStatus, smId)
          : models;
      if (displayedModels?.length > 0) {
        return (
          <TableView<SpoModelsResponseItem>
            data={displayedModels}
            columns={columnsModel}
            smId={smId}
            onCellClick={onCellClick}
            {...checkboxRelatedProps}
          />
        );
      } else {
        return (
          <p data-testid="model-accordion_message_no-model-filtered">
            {t('models.no_model_filtered_message')}
          </p>
        );
      }
    } else {
      return (
        <p data-testid="model-accordion_message_no-model">
          {t('models.no_model_message')}
          {offerDate}
        </p>
      );
    }
  };

  return (
    <VtmnAccordion
      data-testid={`vtmn-accordion-${smId}`}
      summaryComponent={
        <ModelAccordionHeader
          picture={
            isFetching
              ? genericImage
              : (pixlData && pixlData[0]?.imageURL) || genericImage
          }
          smId={smId}
          label={description}
          nbModels={models?.length}
        />
      }
      onClick={() => setIsAccordionDeployed(!isAccordionDeployed)}
    >
      {renderModels(models)}
    </VtmnAccordion>
  );
};
